import { z } from 'zod';
import { idSchema, isoDateSchema, memberSummarySchema, paginationSchema } from './common';
import { rumStatusSchema, rumTypeSchema } from '../enums';
const nameSchema = z.string().trim().min(2, 'Le nom est obligatoire.').max(120);
const abvSchema = z.number().min(0).max(100);
const ageSchema = z.number().int().min(0).max(100);
/**
 * Écran 09 — création de fiche. US-05 : « seuls nom et type obligatoires ».
 * Tout le reste peut arriver vide d'un scan Open Food Facts partiel.
 */
export const createRumSchema = z.object({
    name: nameSchema,
    rumType: rumTypeSchema,
    distillery: z.string().trim().max(120).nullish(),
    country: z.string().trim().max(80).nullish(),
    style: z.string().trim().max(80).nullish(),
    abv: abvSchema.nullish(),
    age: ageSchema.nullish(),
    price: z.number().min(0).max(100000).nullish(),
    photoUrl: z.string().max(2048).nullish(),
    /** Conservé même quand Open Food Facts ne connaît pas la bouteille (US-25). */
    barcode: z.string().trim().max(32).nullish(),
    broughtById: idSchema.nullish(),
});
/** US-07 — modification. Tous les champs sont facultatifs, aucun n'est écrasé par un vide. */
export const updateRumSchema = createRumSchema.partial();
/** Écran 02 — filtres combinables de la Cave (US-06). */
export const searchRumsSchema = paginationSchema.extend({
    /** Recherche plein texte sur nom ET distillerie. */
    q: z.string().trim().max(120).optional(),
    country: z.string().trim().max(80).optional(),
    rumType: rumTypeSchema.optional(),
    status: rumStatusSchema.optional(),
    /** Note moyenne minimale, en dixièmes : « ≥ 8,0 » de la maquette vaut 80. */
    minRating: z.coerce.number().int().min(0).max(100).optional(),
    sessionId: idSchema.optional(),
    sort: z.enum(['recent', 'rating', 'name']).default('recent'),
});
/**
 * Vue d'un rhum telle que l'API la renvoie.
 *
 * En mode aveugle non révélé, `isMasked` vaut true et `name` contient « Rhum mystère n°X » :
 * les champs identifiants sont déjà nuls dans la réponse HTTP. Le front n'a rien à masquer,
 * il n'a jamais reçu l'information.
 */
export const rumViewSchema = z.object({
    id: idSchema,
    name: z.string(),
    distillery: z.string().nullable(),
    country: z.string().nullable(),
    rumType: rumTypeSchema.nullable(),
    style: z.string().nullable(),
    abv: z.number().nullable(),
    age: z.number().nullable(),
    price: z.number().nullable(),
    photoUrl: z.string().nullable(),
    barcode: z.string().nullable(),
    status: rumStatusSchema,
    broughtBy: memberSummarySchema.nullable(),
    /**
     * Qui a saisi la fiche. Null si ce membre a été anonymisé.
     *
     * Exposé parce que le droit de corriger une fiche en dépend (`canEditRum`) : sans lui, le
     * front ne peut pas savoir s'il doit proposer « Modifier » et se contente d'afficher un
     * bouton qui répondra 403 — exactement ce que la table des capacités s'interdit.
     */
    createdBy: memberSummarySchema.nullable(),
    /** Moyenne du club en dixièmes, null tant que personne n'a validé de note. */
    averageScore: z.number().int().nullable(),
    tastingCount: z.number().int().min(0),
    isMasked: z.boolean(),
    createdAt: isoDateSchema,
    updatedAt: isoDateSchema,
});
/** Écran 03 — détail : ajoute les sous-moyennes et les descripteurs agrégés du cercle. */
export const rumDetailSchema = rumViewSchema.extend({
    averageNose: z.number().int().nullable(),
    averagePalate: z.number().int().nullable(),
    averageFinish: z.number().int().nullable(),
    /** Descripteurs les plus cités, du plus fréquent au moins fréquent. */
    topAromaTags: z.array(z.object({ tag: z.string(), count: z.number().int() })),
    lastTastedAt: isoDateSchema.nullable(),
    isClubFavourite: z.boolean(),
});
//# sourceMappingURL=rum.js.map